import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarDays, Car, User as UserIcon, CheckCircle2, Clock, Truck, ShieldCheck, BadgeCheck, Phone, AlertTriangle, FileText, Star } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatZAR, formatRate, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import HandoverPhotos from "../components/HandoverPhotos";
import DamageClaimCard from "../components/DamageClaimCard";

export default function BookingDetail() {
  const { id } = useParams();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (ready && (!user || user === false)) navigate(`/login?next=/bookings/${id}`);
  }, [user, ready, navigate, id]);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/bookings/${id}`).then(async (r) => {
      setBooking(r.data);
      try {
        const v = await api.get(`/vehicles/${r.data.vehicle_id}`);
        setVehicle(v.data);
      } catch (_) {}
    }).catch(() => navigate("/dashboard"));
  }, [id, user?.id, navigate]);
  const reloadBooking = () => api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => {});

  const submitReview = async () => {
    try {
      await api.post("/reviews", { vehicle_id: booking.vehicle_id, booking_id: booking.id, rating, comment });
      toast.success("Thanks for the review!");
      setReviewMode(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit review");
    }
  };

  if (!booking) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#64748B]">Loading booking…</div>;

  const start = new Date(booking.start_date);
  const end = new Date(booking.end_date);
  const isPast = end < new Date();
  const isUpcoming = start > new Date();
  const status = booking.status;

  return (
    <div className="bg-white min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm font-semibold text-[#64748B] hover:text-[#0a1f4d]" data-testid="booking-back">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Booking #{booking.id.slice(0, 8)}</p>
            <h1 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d] mt-2">{booking.vehicle_title}</h1>
            <p className="text-sm text-[#334155] mt-2 inline-flex items-center gap-1.5">
              <CalendarDays size={14} className="text-[#1d7ffd]" />
              {format(start, "EEE, d MMM yyyy")} — {format(end, "EEE, d MMM yyyy")}
            </p>
          </div>
          <StatusBadge status={status} />
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Status timeline + vehicle + host + actions */}
          <div className="lg:col-span-2 space-y-6">
            <Timeline status={status} isPast={isPast} />
            {["confirmed", "completed"].includes(booking.status) && <HandoverPhotos booking={booking} user={user} onChanged={reloadBooking} />}
            {booking.status === "completed" && <DamageClaimCard booking={booking} user={user} onChanged={reloadBooking} />}

            {vehicle && (
              <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white flex gap-4">
                <div className="w-32 sm:w-40 aspect-[4/3] rounded-xl overflow-hidden bg-[#F0F2F5] flex-shrink-0">
                  {vehicle.photos?.[0] ? <img src={vehicle.photos[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#64748B]"><Car size={20}/></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Vehicle</p>
                  <p className="font-display font-bold text-[#0a1f4d] mt-0.5">{vehicle.brand} {vehicle.model}</p>
                  <p className="text-xs text-[#64748B] mt-1 inline-flex items-center gap-1"><MapPin size={11} /> {vehicle.location_text}, {vehicle.city}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {vehicle.inspection_certified && <Chip icon={<BadgeCheck size={10} />} label="Certified" />}
                    {vehicle.insurance_included && <Chip icon={<ShieldCheck size={10} />} label="Insured" />}
                  </div>
                  <Link to={`/vehicles/${vehicle.id}`} className="text-xs font-semibold text-[#1d7ffd] hover:underline mt-2 inline-block">View listing →</Link>
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white">
              <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Pickup & return</p>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">Pickup</p>
                  <p className="text-sm font-semibold text-[#0a1f4d] mt-1">{format(start, "EEE, d MMM yyyy")}</p>
                  <p className="text-xs text-[#64748B]">{format(start, "HH:mm")}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">Return</p>
                  <p className="text-sm font-semibold text-[#0a1f4d] mt-1">{format(end, "EEE, d MMM yyyy")}</p>
                  <p className="text-xs text-[#64748B]">{format(end, "HH:mm")}</p>
                </div>
              </div>
              {booking.with_delivery && (
                <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                  <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] inline-flex items-center gap-1"><Truck size={11} /> Delivery to</p>
                  <p className="text-sm font-semibold text-[#0a1f4d] mt-1">{booking.delivery_address || "Confirmed with host"}</p>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white">
              <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Your host</p>
              <div className="mt-3 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#0a1f4d] text-white flex items-center justify-center font-bold">
                  {(vehicle?.owner_name || "H").slice(0, 1)}
                </div>
                <div className="flex-1">
                  <p className="font-display font-bold text-[#0a1f4d]">{vehicle?.owner_name || "Host"}</p>
                  <p className="text-xs text-[#64748B]">Verified tadada host</p>
                </div>
                {status === "confirmed" ? (
                  <a href="tel:+27110000000" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1d7ffd] border border-[#1d7ffd] rounded-full px-3 py-1.5 hover:bg-[#1d7ffd] hover:text-white transition-colors" data-testid="contact-host">
                    <Phone size={12} /> Contact
                  </a>
                ) : (
                  <button disabled className="inline-flex items-center gap-1 text-xs font-semibold text-[#94A3B8] border border-[#E2E8F0] rounded-full px-3 py-1.5 cursor-not-allowed" data-testid="contact-host" title="Available after booking is confirmed">
                    <Phone size={12} /> Contact
                  </button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white">
              <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Need help?</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button className="text-left p-3 rounded-xl border border-[#E2E8F0] hover:bg-[#F7F8FA]" data-testid="action-roadside">
                  <Phone size={14} className="text-[#1d7ffd]" />
                  <p className="text-sm font-semibold text-[#0a1f4d] mt-2">24/7 roadside</p>
                  <p className="text-[11px] text-[#64748B]">Tap to call</p>
                </button>
                <button className="text-left p-3 rounded-xl border border-[#E2E8F0] hover:bg-[#F7F8FA]" data-testid="action-report-damage">
                  <AlertTriangle size={14} className="text-[#1d7ffd]" />
                  <p className="text-sm font-semibold text-[#0a1f4d] mt-2">Report damage</p>
                  <p className="text-[11px] text-[#64748B]">With photos</p>
                </button>
                <button className="text-left p-3 rounded-xl border border-[#E2E8F0] hover:bg-[#F7F8FA]" data-testid="action-view-agreement">
                  <FileText size={14} className="text-[#1d7ffd]" />
                  <p className="text-sm font-semibold text-[#0a1f4d] mt-2">Rental agreement</p>
                  <p className="text-[11px] text-[#64748B]">Digital copy</p>
                </button>
                {isPast && status === "confirmed" && (
                  <button onClick={() => setReviewMode(true)} className="text-left p-3 rounded-xl border border-[#1d7ffd] bg-[#1d7ffd]/5 hover:bg-[#1d7ffd]/10" data-testid="action-review">
                    <Star size={14} className="text-[#1d7ffd]" />
                    <p className="text-sm font-semibold text-[#0a1f4d] mt-2">Rate this trip</p>
                    <p className="text-[11px] text-[#64748B]">Help the next driver</p>
                  </button>
                )}
              </div>
            </div>

            {reviewMode && (
              <div className="rounded-2xl border border-[#1d7ffd] p-5 bg-[#1d7ffd]/5">
                <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Leave a review</p>
                <div className="mt-3 flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setRating(n)} aria-label={`${n} stars`} data-testid={`rating-${n}`}>
                      <Star size={22} className={`${n <= rating ? "fill-[#1d7ffd] text-[#1d7ffd]" : "text-[#CBD5E1]"}`} />
                    </button>
                  ))}
                </div>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the trip?" className="w-full mt-3 bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 text-sm text-[#0a1f4d] focus:border-[#1d7ffd] outline-none min-h-[80px]" data-testid="review-comment" />
                <div className="mt-3 flex gap-2 justify-end">
                  <button onClick={() => setReviewMode(false)} className="btn-outline !py-2 text-sm">Cancel</button>
                  <button onClick={submitReview} className="btn-primary text-sm" data-testid="submit-review">Submit review</button>
                </div>
              </div>
            )}
          </div>

          {/* Right - Pricing */}
          <aside className="lg:col-span-1">
            {booking.with_driver && (
              <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white mb-4" data-testid="booking-chauffeur-card">
                <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Your chauffeur</p>
                {booking.driver_id ? (
                  <>
                    <div className="mt-3 flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-[#0a1f4d] text-white flex items-center justify-center font-bold">{(booking.driver_name || "D").slice(0, 1)}</span>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#0a1f4d] flex items-center gap-2">
                          {booking.driver_name}
                          {booking.driver_rating_count > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs font-bold text-[#0a1f4d]" data-testid="chauffeur-rating-badge">
                              <Star size={11} className="fill-[#0a1f4d]" /> {booking.driver_rating_avg} <span className="text-[#64748B] font-normal">({booking.driver_rating_count})</span>
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-[#64748B]">{booking.driver_phone || "Contact shared before pickup"} · {booking.driver_status === "completed" ? "Trip completed" : booking.driver_status === "in_progress" ? "Trip in progress" : "Assigned"}</p>
                      </div>
                    </div>
                    {booking.driver_status === "completed" && user?.id === booking.customer_id && (
                      booking.driver_rating ? (
                        <p className="mt-3 text-xs text-[#065F46] bg-[#10B981]/10 rounded-lg px-3 py-2" data-testid="chauffeur-rated">
                          You rated {booking.driver_name} {booking.driver_rating}/5{booking.driver_rating_comment ? ` — “${booking.driver_rating_comment}”` : ""}
                        </p>
                      ) : (
                        <DriverRatingForm bookingId={booking.id} onDone={(r) => setBooking((b) => ({ ...b, driver_rating: r.review.rating, driver_rating_comment: r.review.comment, driver_rating_avg: r.rating_avg, driver_rating_count: r.rating_count }))} />
                      )
                    )}
                  </>
                ) : (
                  <p className="mt-2 text-sm text-[#334155]">We're matching a verified chauffeur to your trip. You'll be notified as soon as one accepts.</p>
                )}
              </div>
            )}
            <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white sticky top-24">
              <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Receipt</p>
              <div className="mt-4 space-y-2 text-sm text-[#334155]">
                <Row label={`Rental × ${booking.pricing?.days} days`} value={formatZAR(booking.pricing?.rental)} />
                {booking.pricing?.insurance > 0 && <Row label="Premium insurance" value={formatZAR(booking.pricing.insurance)} />}
                {booking.pricing?.driver > 0 && <Row label="Chauffeur" value={formatZAR(booking.pricing.driver)} />}
                {booking.pricing?.delivery > 0 && <Row label="Delivery" value={formatZAR(booking.pricing.delivery)} />}
                <Row label="Service fee" value={formatZAR(booking.pricing?.service_fee)} />
                <Row label="Refundable deposit" value={formatZAR(booking.pricing?.deposit)} subtle />
                {booking.pricing?.km_included > 0 && (
                  <p className="rounded-xl bg-[#F4F8FF] px-3 py-2 text-xs text-[#0a1f4d]" data-testid="receipt-km-allowance">
                    <b>{booking.pricing.km_included.toLocaleString()} km included</b> ({booking.pricing.km_per_day} km/day × {booking.pricing.days}) · {formatRate(booking.pricing.excess_km_rate)}/km over
                  </p>
                )}
                <div className="flex items-baseline justify-between pt-3 border-t border-[#E2E8F0]">
                  <span className="font-display font-bold text-[#0a1f4d]">Total paid</span>
                  <span className="font-display text-xl font-black text-[#0a1f4d]">{formatZAR(booking.pricing?.total_due)}</span>
                </div>
              </div>
              {booking.deposit_status === "held" && (
                <div className="mt-5 p-3 rounded-xl bg-[#FFFBEB] text-[#78350F] text-xs" data-testid="deposit-held">
                  <p className="font-bold">Deposit on hold</p>
                  <p className="mt-0.5">A damage claim is open on this trip. Your {formatZAR(booking.pricing?.deposit)} deposit is held until tadada decides.</p>
                </div>
              )}
              {booking.deposit_status === "released" && (
                <div className="mt-5 p-3 rounded-xl bg-[#10B981]/10 text-[#065F46] text-xs space-y-1" data-testid="deposit-released">
                  <p className="font-bold flex items-center gap-1"><CheckCircle2 size={14} /> Deposit released — {formatZAR(booking.deposit_refund)} refunded</p>
                  {booking.late_fee > 0 && <p className="text-[#B45309]">Late return: {booking.late_hours}h after the 1h grace → {formatZAR(booking.late_fee)} fee deducted.</p>}
                  {booking.km_driven != null && <p data-testid="deposit-km-line">Kilometres: {booking.km_driven.toLocaleString()} km driven of {Number(booking.pricing?.km_included || 0).toLocaleString()} km included.</p>}
                  {booking.excess_km_fee > 0 && <p className="text-[#B45309]" data-testid="deposit-excess-km">Excess kilometres: {booking.excess_km.toLocaleString()} km × {formatRate(booking.pricing?.excess_km_rate)} → {formatZAR(booking.excess_km_fee)} deducted.</p>}
                  {booking.claim_credit > 0 && <p className="text-[#B45309]">Damage claim: {formatZAR(booking.claim_credit)} deducted (approved by tadada).</p>}
                  {booking.deposit_outstanding > 0 && <p className="text-[#B91C1C]">Outstanding balance: {formatZAR(booking.deposit_outstanding)}</p>}
                  <p className="text-[#065F46]/80">Returned {format(new Date(booking.returned_at), "d MMM, HH:mm")}. Refunds reach your card in 3–5 business days.</p>
                </div>
              )}
              {status === "confirmed" && isUpcoming && (
                <div className="mt-5 p-3 rounded-xl bg-[#10B981]/10 text-[#065F46] text-xs">
                  <CheckCircle2 size={14} className="inline mr-1" /> All set! See you on {format(start, "EEE d MMM")}.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, subtle }) => (
  <div className={`flex items-center justify-between ${subtle ? "text-[#64748B]" : "text-[#334155]"}`}>
    <span>{label}</span>
    <span className="font-semibold text-[#0a1f4d]">{value}</span>
  </div>
);

const Chip = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1 bg-[#F0F2F5] text-[#0a1f4d] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md">
    <span className="text-[#1d7ffd]">{icon}</span> {label}
  </span>
);

function DriverRatingForm({ bookingId, onDone }) {
  const [stars, setStars] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/rate-driver`, { rating: stars, comment: text });
      toast.success("Thanks for rating your chauffeur");
      onDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit rating");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded-xl border border-[#1d7ffd]/40 bg-[#1d7ffd]/5 p-3" data-testid="driver-rating-form">
      <p className="text-xs font-bold text-[#0a1f4d]">How was your chauffeur?</p>
      <div className="mt-2 flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setStars(n)} aria-label={`${n} stars`} data-testid={`driver-rating-${n}`}>
            <Star size={20} className={n <= stars ? "fill-[#1d7ffd] text-[#1d7ffd]" : "text-[#CBD5E1]"} />
          </button>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Punctual, friendly, safe driving…" className="w-full mt-2 bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-xs text-[#0a1f4d] focus:border-[#1d7ffd] outline-none min-h-[60px]" data-testid="driver-rating-comment" />
      <button onClick={submit} disabled={busy} className="btn-primary text-xs !py-2 mt-2 w-full disabled:opacity-60" data-testid="driver-rating-submit">{busy ? "Sending…" : "Submit rating"}</button>
    </div>
  );
}


const StatusBadge = ({ status }) => {
  const map = {
    confirmed: { bg: "bg-[#10B981]/15", text: "text-[#10B981]", label: "Confirmed" },
    pending_payment: { bg: "bg-[#F59E0B]/15", text: "text-[#B45309]", label: "Pending payment" },
    cancelled: { bg: "bg-[#EF4444]/15", text: "text-[#B91C1C]", label: "Cancelled" },
    completed: { bg: "bg-[#0a1f4d]/10", text: "text-[#0a1f4d]", label: "Completed" },
  };
  const s = map[status] || { bg: "bg-[#F0F2F5]", text: "text-[#0a1f4d]", label: status };
  return <span className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-md ${s.bg} ${s.text}`}>{s.label}</span>;
};

function Timeline({ status, isPast }) {
  const steps = [
    { key: "booked", label: "Booked", desc: "Payment confirmed", icon: <CheckCircle2 size={14} /> },
    { key: "ready", label: "Ready for pickup", desc: "Meet your host", icon: <Car size={14} /> },
    { key: "driving", label: "On the road", desc: "Enjoy the trip", icon: <MapPin size={14} /> },
    { key: "returned", label: "Returned", desc: "Deposit released", icon: <CheckCircle2 size={14} /> },
  ];
  let activeIdx = 0;
  if (status === "confirmed") activeIdx = isPast ? 3 : 1;
  if (status === "completed") activeIdx = 3;
  return (
    <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white">
      <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Trip status</p>
      <div className="mt-4 grid grid-cols-4 gap-2">
        {steps.map((s, i) => (
          <div key={s.key} className="text-center">
            <div className={`mx-auto w-9 h-9 rounded-full flex items-center justify-center ${i <= activeIdx ? "bg-[#1d7ffd] text-white" : "bg-[#F0F2F5] text-[#64748B]"}`}>
              {i < activeIdx ? <CheckCircle2 size={16} /> : s.icon}
            </div>
            <p className={`text-[11px] font-bold uppercase tracking-wide mt-2 ${i <= activeIdx ? "text-[#0a1f4d]" : "text-[#64748B]"}`}>{s.label}</p>
            <p className="text-[10px] text-[#64748B] mt-0.5">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
